import { useMemo, useState } from "react";
import { Route, Routes, useParams } from "react-router-dom";

import { trans } from "translations";

import { Loader } from "components/elements";
import {
  PageContent,
  PageContentError,
  PageError,
  PageHeader,
  TabContent,
} from "components/layout";
import { withActiveOrganization } from "containers/Auth/decorators/withActiveOrganization";
import { ALL_MODERATORS, checkAccess, ROLES } from "containers/Permissions";
import {
  TiVoAppVariantVersion,
  useProvider,
  useVariant,
  useVariantDraft,
} from "hooks/tivoApps";

import { VARIANT_DRAFT_ID } from "../constants";
import { TABS } from "./constants";
import { TiVoAppDetailsTabs } from "./TiVoAppDetailsTabs";
import { getPageHeader } from "./utils";

type WithActiveOrganizationProps = {
  activeOrganization: object;
};

export function TiVoAppDetailsComponent({
  activeOrganization,
}: WithActiveOrganizationProps) {
  const { id, variantId } = useParams();
  const moderator = checkAccess({ activeOrganization }, [
    ...ALL_MODERATORS,
    ROLES.tivo.preview,
  ]) as boolean;

  const variantMode = variantId !== undefined;
  //** This flag determines if user is in details page for draft without any existing variant asigned to it */
  const draftOnlyMode = variantId === VARIANT_DRAFT_ID;

  const {
    data: providerData,
    isFetching: providerLoading,
    error: providerError,
  } = useProvider(id, {
    include: ["variants", "variantDraft", "presentations"],
  });
  const {
    data: variantData,
    isLoading: variantLoading,
    error: variantError,
  } = useVariant(id, draftOnlyMode ? undefined : variantId, {
    include: ["draftVersion", "versions"],
  });
  const [selectedVariantVersionIndex, setSelectedVariantVersionIndex] =
    useState(0);

  const variantDraft = useVariantDraft(draftOnlyMode ? id : undefined);

  const header = useMemo(() => {
    return getPageHeader({
      id,
      appName: providerData?.results?.label,
      variantName: variantData?.results?.label,
    });
  }, [providerData?.results?.label, id, variantData?.results?.label]);

  const variantVersions = useMemo(() => {
    const variants = draftOnlyMode
      ? [variantDraft.data?.results]
      : variantData?.results?.versions || [];
    return variants.filter(
      (variant): variant is TiVoAppVariantVersion => variant !== undefined
    );
  }, [variantDraft.data, variantData?.results?.versions, draftOnlyMode]);

  const tabs = useMemo(() => {
    if (draftOnlyMode) {
      return [TABS.VARIANT, TABS.APPLICATION];
    }
    if (variantMode) {
      return [TABS.VARIANT, TABS.APPLICATION, TABS.PUBLICATIONS];
    }
    return [
      TABS.APPLICATION,
      TABS.VARIANTS,
      ...(moderator ? [TABS.MANIFESTS] : []),
    ];
  }, [moderator, draftOnlyMode, variantMode]);

  if (providerLoading || variantLoading) {
    return <Loader />;
  }

  if (providerError || variantError) {
    return (
      <PageError pageHeader={header} error={providerError || variantError} />
    );
  }

  if (
    !id ||
    !providerData ||
    !providerData.results ||
    (variantMode && !variantData?.results && !variantDraft.data)
  ) {
    return (
      <PageError
        pageHeader={header}
        error={trans.DEFAULT_REQUEST_ERROR_MESSAGE()}
      />
    );
  }

  return (
    <>
      <PageHeader>{header}</PageHeader>
      <PageContent>
        <TiVoAppDetailsTabs tabs={tabs} />
        <TabContent>
          <Routes>
            {tabs.map(({ Component, path }) => (
              <Route
                key={path}
                path={path}
                element={
                  <Component
                    provider={providerData.results}
                    variant={variantData?.results}
                    variantVersions={variantVersions}
                    selectedVariantVersionIndex={selectedVariantVersionIndex}
                    onVariantVersionChange={setSelectedVariantVersionIndex}
                  />
                }
              />
            ))}
            <Route
              path="*"
              element={<PageContentError error={{ isForbidden: true }} />}
            />
          </Routes>
        </TabContent>
      </PageContent>
    </>
  );
}

export const TiVoAppDetails = withActiveOrganization(TiVoAppDetailsComponent);
